/**
 * Compute the runs of free wall length where a kitchen cabinet can sit.
 * Pure: takes a wall + the openings on it and returns parametric ranges
 * along the wall axis (from `alignment.bottomLeft`, in meters).
 */

// Hoisted from the GLOBAL `Math` so hot loops below reference a module-scope
// binding. A global lookup is not inline-cacheable inside a `vm` context
// (how jest isolates modules), which made these loops ~100x slower under
// test than in production. Exactly semantics-preserving: every `Math`
// member is a pure static that never reads `this`.
const { hypot2 } = require('../geometry/hypot2');
const MATH_MAX = Math.max;
const MATH_MIN = Math.min;

const {
  OPENING_BUFFER,
  MIN_CABINET_WIDTH,
  MIN_STANDALONE_CABINET_WIDTH,
  KITCHEN_CABINET_DIMENSIONS,
  CORRIDOR_OVERLAP_TOLERANCE,
} = require('./constants');

/**
 * For each corridor rectangle that sits within cabinet depth of the wall face,
 * returns the wall-along range that is blocked by the corridor.
 *
 * @param {Object} wall - Wall with `alignment.bottomLeft` / `bottomRight`.
 * @param {Array<Array<{x: number, z: number}>>} corridorRectangles - Each
 *   element is an array of ≥3 corner points (axis-aligned rect) in world space.
 * @param {number} cabinetDepth - Maximum perpendicular distance (meters) at
 *   which a corridor's nearest face is considered to intrude on the cabinet zone.
 * @returns {Array<{start: number, end: number}>} Wall-parameter ranges to block.
 */
function corridorBlockedRangesForWall(wall, corridorRectangles, cabinetDepth) {
  const wallStart = wall.alignment?.bottomLeft;
  const wallEnd = wall.alignment?.bottomRight;
  if (!wallStart || !wallEnd) return [];

  const dx = wallEnd.x - wallStart.x;
  const dz = wallEnd.z - wallStart.z;
  const wallLen = hypot2(dx, dz);
  if (wallLen <= 0) return [];

  // Unit vector along wall (u-axis) and perpendicular (n-axis).
  const ux = dx / wallLen;
  const uz = dz / wallLen;
  const nx = -uz;
  const nz = ux;

  const ranges = [];
  for (const rect of corridorRectangles || []) {
    if (!Array.isArray(rect) || rect.length < 3) continue;

    let uMin = Infinity,
      uMax = -Infinity;
    let nMin = Infinity,
      nMax = -Infinity;
    for (const p of rect) {
      const u = (p.x - wallStart.x) * ux + (p.z - wallStart.z) * uz;
      const n = (p.x - wallStart.x) * nx + (p.z - wallStart.z) * nz;
      if (u < uMin) uMin = u;
      if (u > uMax) uMax = u;
      if (n < nMin) nMin = n;
      if (n > nMax) nMax = n;
    }

    // Closest perpendicular distance from the wall line to the rect,
    // measured ONLY on the cabinet-body side (positive normal). The
    // n-axis points along `wallNormal`, which is the cabinet body's
    // inward direction. A corridor on the OUTWARD side (n < 0) sits
    // behind the wall and doesn't interfere with cabinets at all —
    // skip it. This matters for virtual peninsula walls, where the
    // outward side is INSIDE the room (the cook side) and walkway
    // corridors routinely run there.
    if (nMax <= 0) continue; // entire corridor on the outward side
    let nClosest;
    if (nMin <= 0) {
      nClosest = 0; // rect straddles wall line — touches cabinet side
    } else {
      nClosest = nMin; // both extents on cabinet side; nearest is nMin
    }

    // Allow up to CORRIDOR_OVERLAP_TOLERANCE of cabinet/corridor overlap
    // before considering the wall blocked. Tight kitchens routinely have
    // corridor envelopes that intrude on the cabinet zone by an inch or
    // two — accepting that small overlap keeps those rooms layoutable.
    const eps = 1e-3;
    if (nClosest >= cabinetDepth - CORRIDOR_OVERLAP_TOLERANCE - eps) continue;

    const lo = MATH_MAX(0, uMin);
    const hi = MATH_MIN(wallLen, uMax);
    if (hi > lo) ranges.push({ start: lo, end: hi });
  }
  return ranges;
}

/**
 * @param {Object} wall - Wall with `alignment.bottomLeft` / `bottomRight`.
 * @param {Array<Object>} openings - Openings whose `parentWallIds[0]`
 *   matches `wall.id`. Each must have `center` and `dimensions.x`
 *   (width along the wall axis). Caller is responsible for filtering
 *   out non-blocking openings (e.g. windows that don't reach the floor).
 * @param {Array<{start: number, end: number}>} extraBlockedRanges - Additional
 *   wall-parameter ranges (in meters from `bottomLeft`) to subtract from the
 *   available space. Used to reserve the island's 36" aisle clearance zone.
 * @param {Array<Array<{x: number, z: number}>>} corridorRectangles - Axis-aligned
 *   corridor rectangles (each an array of corner points). Any corridor whose
 *   nearest face is within cabinet depth of the wall face blocks the
 *   corresponding wall-along range from receiving cabinets.
 * @returns {Array<{start: number, end: number}>} Ranges along the wall
 *   axis ≥ MIN_CABINET_WIDTH wide. Empty if the wall is too short or
 *   fully blocked.
 */
function getAvailableWallSegments(
  wall,
  openings,
  extraBlockedRanges = [],
  corridorRectangles = [],
  cabinetDepth = KITCHEN_CABINET_DIMENSIONS.depth
) {
  const start = wall.alignment?.bottomLeft;
  const end = wall.alignment?.bottomRight;
  if (!start || !end) return [];

  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const wallLen = hypot2(dx, dz);
  if (wallLen <= 0) return [];

  const wallDir = { x: dx / wallLen, z: dz / wallLen };

  const blocked = [];
  for (const opening of openings || []) {
    if (!opening?.center || !opening?.dimensions) continue;

    const proj =
      (opening.center.x - start.x) * wallDir.x +
      (opening.center.z - start.z) * wallDir.z;
    const halfWidth = (opening.dimensions.x || 0) / 2;
    // Skip openings whose BODY lies entirely off this wall's span — e.g.
    // a window on the non-kitchen part of a wall that was clipped to the
    // kitchen section (its parentWallId still points here). Only its
    // clearance buffer would bleed onto the wall, which would spuriously
    // sterilize the corner and drop an L to a single-sided / blind
    // corner. The opening isn't physically in front of any cabinet here.
    if (proj + halfWidth <= 0 || proj - halfWidth >= wallLen) continue;
    const blockStart = MATH_MAX(0, proj - halfWidth - OPENING_BUFFER);
    const blockEnd = MATH_MIN(wallLen, proj + halfWidth + OPENING_BUFFER);
    if (blockEnd > blockStart)
      blocked.push({ start: blockStart, end: blockEnd });
  }

  for (const range of extraBlockedRanges || []) {
    if (!range) continue;
    const blockStart = MATH_MAX(0, range.start);
    const blockEnd = MATH_MIN(wallLen, range.end);
    if (blockEnd > blockStart)
      blocked.push({ start: blockStart, end: blockEnd });
  }

  const corridorRanges = corridorBlockedRangesForWall(
    wall,
    corridorRectangles,
    cabinetDepth
  );
  for (const range of corridorRanges) {
    blocked.push({ start: range.start, end: range.end });
  }

  blocked.sort((a, b) => a.start - b.start);
  const merged = [];
  for (const range of blocked) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) {
      last.end = MATH_MAX(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }

  const segments = [];
  let cursor = 0;
  for (const range of merged) {
    if (range.start > cursor)
      segments.push({ start: cursor, end: range.start });
    cursor = MATH_MAX(cursor, range.end);
  }
  if (cursor < wallLen) segments.push({ start: cursor, end: wallLen });

  // A stretch this wide can host a real box. 9" (`MIN_STANDALONE_CABINET_WIDTH`)
  // rather than 12": the narrowest cabinet the codebase sells is 9", so a 9-12"
  // stretch is buildable and used to be discarded as unusable wall.
  return segments.filter(
    (s) => s.end - s.start >= MIN_STANDALONE_CABINET_WIDTH
  );
}

module.exports = { getAvailableWallSegments, corridorBlockedRangesForWall };
